export const AFFILIATE_STORAGE_KEY = 'fln_affiliate_data';
export const AFFILIATE_COOKIE_DURATION = 60 * 24 * 60 * 60 * 1000; // 60 days in milliseconds
export const AFFILIATE_COOKIE_NAME = 'fln_ref';

// Helper function to get cookie value by name
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Helper function to set cookie with proper expiration
export const setCookie = (name, value, durationMs) => {
  const expirationDate = new Date(Date.now() + durationMs);
  document.cookie = `${name}=${value}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
  return expirationDate;
};

// Set affiliate cookie with expiration timestamp embedded in the value
export const setAffiliateCookie = (refCode, durationMs) => {
  const now = Date.now();
  const expiresAt = now + durationMs;
  
  // Create cookie value with both refCode and expiration timestamp
  const cookieValue = `${refCode}|${expiresAt}`;
  
  // Set the cookie with browser expiration (as backup)
  const browserExpirationDate = new Date(expiresAt);
  document.cookie = `${AFFILIATE_COOKIE_NAME}=${cookieValue}; expires=${browserExpirationDate.toUTCString()}; path=/; SameSite=Lax`;
  
  console.log(`🍪 Set affiliate cookie: ${AFFILIATE_COOKIE_NAME}=${cookieValue}`);
  console.log(`📅 Cookie expires: ${browserExpirationDate.toISOString()}`);
  
  return {
    refCode,
    expiresAt,
    expirationDate: browserExpirationDate.toISOString()
  };
};

// Get affiliate data from cookie and check expiration
export const getAffiliateCookieData = () => {
  try {
    const cookieValue = getCookie(AFFILIATE_COOKIE_NAME);
    if (!cookieValue) {
      console.log('🚫 No affiliate cookie found');
      return null;
    }
    
    // Parse cookie value (format: "refCode|expirationTimestamp")
    const [refCode, expiresAtStr] = cookieValue.split('|');
    
    if (!refCode || !expiresAtStr) {
      console.log('🚫 Invalid cookie format, removing corrupted cookie');
      removeCookie(AFFILIATE_COOKIE_NAME);
      return null;
    }
    
    const expiresAt = parseInt(expiresAtStr);
    const now = Date.now();
    
    // Check if cookie has expired
    if (now > expiresAt) {
      console.log(`⏰ Affiliate cookie expired. Removing cookie for: ${refCode}`);
      removeCookie(AFFILIATE_COOKIE_NAME);
      return null;
    }
    
    const timeUntilExpiry = expiresAt - now;
    const daysUntilExpiry = Math.floor(timeUntilExpiry / (24 * 60 * 60 * 1000));
    
    console.log(`✅ Valid affiliate cookie found: ${refCode}, expires in ${daysUntilExpiry} days`);
    
    return {
      refCode,
      expiresAt,
      expirationDate: new Date(expiresAt).toISOString(),
      timeUntilExpiry,
      daysUntilExpiry,
      valid: true
    };
  } catch (error) {
    console.error('Error parsing affiliate cookie:', error);
    removeCookie(AFFILIATE_COOKIE_NAME);
    return null;
  }
};

// Helper function to remove cookie
export const removeCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// Refresh affiliate cookie expiration (same refCode)
export const refreshAffiliateCookie = (existingCookieData) => {
  console.log(`🔄 Refreshing affiliate cookie expiration for: ${existingCookieData.refCode}`);
  
  // Set new cookie with refreshed expiration
  const refreshedCookie = setAffiliateCookie(existingCookieData.refCode, AFFILIATE_COOKIE_DURATION);
  
  console.log(`✅ Cookie expiration refreshed for: ${existingCookieData.refCode}`);
  console.log(`📅 New expiration: ${refreshedCookie.expirationDate}`);
  
  return refreshedCookie;
};

export const setAffiliateData = (refCode, source = 'direct') => {
  const now = Date.now();
  
  // Set the affiliate cookie with embedded expiration
  const cookieData = setAffiliateCookie(refCode, AFFILIATE_COOKIE_DURATION);
  
  // Store minimal data in localStorage for additional tracking (optional)
  const affiliateData = {
    refCode,
    source,
    timestamp: now,
    visits: 1,
    cookieExpirationDate: cookieData.expirationDate
  };
  
  localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(affiliateData));
  
  console.log(`✅ Affiliate data set for: ${refCode}`);
  console.log(`📅 Cookie expires: ${cookieData.expirationDate}`);
  
  return {
    ...affiliateData,
    expiresAt: cookieData.expiresAt
  };
};

export const getAffiliateData = () => {
  // Primary check: get data from cookie (authoritative source)
  const cookieData = getAffiliateCookieData();
  if (!cookieData) {
    // Clean up localStorage if cookie is gone/expired
    if (localStorage.getItem(AFFILIATE_STORAGE_KEY)) {
      console.log('🧹 Cleaning up localStorage - cookie expired/missing');
      localStorage.removeItem(AFFILIATE_STORAGE_KEY);
    }
    return null;
  }
  
  // Get additional data from localStorage if available
  try {
    const stored = localStorage.getItem(AFFILIATE_STORAGE_KEY);
    if (stored) {
      const localData = JSON.parse(stored);
      // Merge cookie data (authoritative) with localStorage data (additional info)
      return {
        ...localData,
        refCode: cookieData.refCode, // Cookie is authoritative for refCode
        expiresAt: cookieData.expiresAt, // Cookie is authoritative for expiration
        cookieExpirationDate: cookieData.expirationDate,
        timeUntilExpiry: cookieData.timeUntilExpiry,
        daysUntilExpiry: cookieData.daysUntilExpiry
      };
    }
  } catch (error) {
    console.error('Error reading localStorage data:', error);
  }
  
  // Return cookie data even if localStorage is missing/corrupted
  return {
    refCode: cookieData.refCode,
    expiresAt: cookieData.expiresAt,
    cookieExpirationDate: cookieData.expirationDate,
    timeUntilExpiry: cookieData.timeUntilExpiry,
    daysUntilExpiry: cookieData.daysUntilExpiry,
    visits: 1,
    timestamp: Date.now() - cookieData.timeUntilExpiry + AFFILIATE_COOKIE_DURATION // Approximate
  };
};

export const updateAffiliateVisit = () => {
  const existing = getAffiliateData();
  if (existing) {
    existing.visits = (existing.visits || 1) + 1;
    existing.lastVisit = Date.now();
    localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(existing));
    return existing;
  }
  return null;
};


export const removeAffiliateData = () => {
  localStorage.removeItem(AFFILIATE_STORAGE_KEY);
  removeCookie(AFFILIATE_COOKIE_NAME);
  console.log('🗑️ Removed affiliate data and cookie');
};

export const processAffiliateParams = (searchParams) => {
  // Check for affiliate parameters in URL
  const newRefCode = searchParams.get('ref') || searchParams.get('affiliate') || searchParams.get('partner');
  const source = searchParams.get('source') || searchParams.get('utm_source') || 'direct';
  const campaign = searchParams.get('campaign') || searchParams.get('utm_campaign');
  const medium = searchParams.get('medium') || searchParams.get('utm_medium');
  
  // Enhanced logging for testing
  console.log('🔍 AFFILIATE TRACKING DEBUG:');
  console.log('📝 URL Search Params:', window.location.search);
  console.log('🎯 Extracted Parameters:', {
    newRefCode,
    source,
    campaign,
    medium,
    allParams: Object.fromEntries(searchParams.entries())
  });
  
  // Check for existing valid affiliate cookie (primary source of truth)
  const existingCookieData = getAffiliateCookieData();
  console.log('🍪 Existing cookie data:', existingCookieData);
  
  // If there's a new ref code in the URL
  if (newRefCode) {
    console.log(`🆕 New referral code found in URL: ${newRefCode}`);
    
    // If we have existing valid cookie data
    if (existingCookieData) {
      console.log(`🔍 Existing cookie found for: ${existingCookieData.refCode}`);
      
      // Same ref code - refresh the cookie expiration
      if (existingCookieData.refCode === newRefCode) {
        console.log('✅ Same ref code - refreshing cookie expiration');
        const refreshedCookie = refreshAffiliateCookie(existingCookieData);
        
        // Update localStorage with additional data
        const localData = JSON.parse(localStorage.getItem(AFFILIATE_STORAGE_KEY) || '{}');
        const updatedData = {
          ...localData,
          refCode: newRefCode,
          lastVisit: Date.now(),
          visits: (localData.visits || 1) + 1,
          cookieExpirationDate: refreshedCookie.expirationDate,
          refreshedAt: Date.now()
        };
        
        // Add new campaign/medium data if provided
        if (campaign && !updatedData.campaign) updatedData.campaign = campaign;
        if (medium && !updatedData.medium) updatedData.medium = medium;
        
        localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(updatedData));
        
        console.log('🔄 Refreshed affiliate data:', updatedData);
        return {
          ...updatedData,
          expiresAt: refreshedCookie.expiresAt
        };
      } 
      // Different ref code - ignore new one, keep existing
      else {
        console.log(`⚠️ Different ref code detected. Keeping existing: ${existingCookieData.refCode}, ignoring new: ${newRefCode}`);
        console.log('📅 Existing cookie expires:', existingCookieData.expirationDate);
        console.log(`⏰ Days until expiry: ${existingCookieData.daysUntilExpiry}`);
        
        // Just update visit count for existing data
        const updated = updateAffiliateVisit();
        console.log('📊 Updated existing affiliate visit count:', updated);
        return updated;
      }
    } 
    // No existing cookie - set new affiliate data
    else {
      console.log('🆕 No existing cookie found - setting new affiliate data');
      const affiliateData = setAffiliateData(newRefCode, source);
      
      // Add campaign and medium data to localStorage
      if (campaign || medium) {
        const localData = JSON.parse(localStorage.getItem(AFFILIATE_STORAGE_KEY) || '{}');
        if (campaign) localData.campaign = campaign;
        if (medium) localData.medium = medium;
        localStorage.setItem(AFFILIATE_STORAGE_KEY, JSON.stringify(localData));
      }
      
      console.log('✅ New affiliate data stored:', affiliateData);
      console.log('🍪 Cookie value:', getCookie(AFFILIATE_COOKIE_NAME));
      console.log('💾 LocalStorage data:', localStorage.getItem(AFFILIATE_STORAGE_KEY));
      
      return affiliateData;
    }
  }
  // No new ref code in URL
  else {
    console.log('❌ No referral code found in URL parameters');
    
    // Check if we have existing cookie to update visit count
    if (existingCookieData) {
      console.log('📈 Updating existing affiliate visit count');
      const updated = updateAffiliateVisit();
      console.log('📊 Updated affiliate data:', updated);
      return updated;
    } else {
      console.log('🚫 No existing affiliate cookie to update');
      return null;
    }
  }
};

export const isValidRefCode = (refCode) => {
  // Basic validation - can be enhanced based on your ref code format
  return refCode && typeof refCode === 'string' && refCode.length >= 3 && refCode.length <= 50;
};

export const getAffiliateReferralData = () => {
  const affiliate = getAffiliateData();
  if (!affiliate) return null;
  
  return {
    refCode: affiliate.refCode,
    source: affiliate.source,
    campaign: affiliate.campaign,
    medium: affiliate.medium,
    firstVisit: new Date(affiliate.timestamp).toISOString(),
    lastVisit: affiliate.lastVisit ? new Date(affiliate.lastVisit).toISOString() : null,
    totalVisits: affiliate.visits || 1,
    daysTracked: Math.floor((Date.now() - affiliate.timestamp) / (24 * 60 * 60 * 1000))
  };
};

// Helper function for testing in browser console
export const debugAffiliateTracking = () => {
  console.log('🧪 AFFILIATE TRACKING DEBUG REPORT:');
  console.log('==========================================');
  
  // Current URL and params
  const currentUrl = window.location.href;
  const searchParams = new URLSearchParams(window.location.search);
  console.log('🌐 Current URL:', currentUrl);
  console.log('🔗 Current Search Params:', Object.fromEntries(searchParams.entries()));
  
  // LocalStorage data
  const storedData = localStorage.getItem(AFFILIATE_STORAGE_KEY);
  console.log('💾 LocalStorage Raw Data:', storedData);
  
  // Parsed affiliate data
  const affiliateData = getAffiliateData();
  console.log('📊 Parsed Affiliate Data:', affiliateData);
  
  // Cookie data
  const cookies = document.cookie.split(';').map(c => c.trim());
  const flnRefCookie = getCookie(AFFILIATE_COOKIE_NAME);
  console.log('🍪 All Cookies:', cookies);
  console.log('🎯 FLN Ref Cookie Value:', flnRefCookie);
  
  // Check cookie vs localStorage consistency
  if (affiliateData && flnRefCookie) {
    const consistent = affiliateData.refCode === flnRefCookie;
    console.log('🔄 Cookie-LocalStorage Consistency:', consistent ? '✅ Match' : '❌ Mismatch');
  }
  
  // Cookie-based affiliate data check (primary source)
  const cookieData = getAffiliateCookieData();
  console.log('🔍 Cookie-based Data Check:', cookieData);
  
  // Referral data
  const referralData = getAffiliateReferralData();
  console.log('📈 Referral Data:', referralData);
  
  // Expiration check
  if (affiliateData) {
    const now = Date.now();
    const timeUntilExpiry = affiliateData.expiresAt - now;
    const daysUntilExpiry = Math.floor(timeUntilExpiry / (24 * 60 * 60 * 1000));
    const hoursUntilExpiry = Math.floor((timeUntilExpiry % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    
    console.log('⏰ Expiration Details:', {
      durationDays: 60, // Cookie duration in days
      storedExpirationDate: affiliateData.cookieExpirationDate,
      millisecondsUntilExpiry: timeUntilExpiry,
      daysUntilExpiry,
      hoursUntilExpiry,
      expired: timeUntilExpiry <= 0,
      refreshedAt: affiliateData.refreshedAt ? new Date(affiliateData.refreshedAt).toISOString() : 'Never'
    });
  }
  
  console.log('==========================================');
  return {
    url: currentUrl,
    params: Object.fromEntries(searchParams.entries()),
    localStorage: storedData,
    affiliate: affiliateData,
    referral: referralData,
    cookie: flnRefCookie,
    cookieData,
    consistent: affiliateData && flnRefCookie ? affiliateData.refCode === flnRefCookie : null
  };
};

// Make debug function available globally for console testing
if (typeof window !== 'undefined') {
  window.debugAffiliateTracking = debugAffiliateTracking;
}